import { m4 } from "./matrix";
import { GRAYSCALE_MODES } from "../../config/config";
import { getWebglContext, hasCustomPixelColorSettings, isContextLost } from "../canvasUtils";
import ColorWebglProgram from "./colorWebglProgram";
import OptosWebglProgram from "./optosWebglProgram";
import PaletteWebglProgram from "./paletteWebglProgram";
import defaultVertexShader from "./default.vert";
import defaultFragmentShader from "./default.frag";
import colorFragmentShader from "./color.frag";
import lumaFragmentShader from "./luma.frag";
import redFragmentShader from "./red.frag";
import greenFragmentShader from "./green.frag";
import blueFragmentShader from "./blue.frag";
import optosDefaultFragmentShader from "./optosDefault.frag";
import optosGreenFragmentShader from "./optosGreen.frag";
import invertFragmentShader from "./invert.frag";
import paletteFragmentShader from "./palette.frag";
import linearPaletteFragmentShader from "./linearPalette.frag";
import WebglProgram from "./webglProgram";
import LinearPaletteWebglProgram from "./linearPaletteWebglProgram";
import { sizesEqual } from "../geometry";

export default class WebglRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.canvasSize = null;
    this.imgSize = null;
    this.transform = null;
    this.isFlipped = false;
    this.isFlipped90 = false;
    this.isFlipped270 = false;
    this.flippedX = false;
    this.colorSettings = null;
    this.octColorSettings = null;
    this.thicknessMapColorSettings = null;
    this.componentName = null;
    this.isSideBySide = false;
    this.forceTranslate = false;
    
    this.programs = {
      standard: new WebglProgram(defaultVertexShader, defaultFragmentShader),
      color: new ColorWebglProgram(defaultVertexShader, colorFragmentShader),
      luma: new ColorWebglProgram(defaultVertexShader, lumaFragmentShader),
      red: new ColorWebglProgram(defaultVertexShader, redFragmentShader),
      green: new ColorWebglProgram(defaultVertexShader, greenFragmentShader),
      blue: new ColorWebglProgram(defaultVertexShader, blueFragmentShader),
      optosStandard: new OptosWebglProgram(defaultVertexShader, optosDefaultFragmentShader),
      optosGreen: new OptosWebglProgram(defaultVertexShader, optosGreenFragmentShader),
      invert: new WebglProgram(defaultVertexShader, invertFragmentShader),
      palette: new PaletteWebglProgram(defaultVertexShader, paletteFragmentShader),
      linearPalette: new LinearPaletteWebglProgram(defaultVertexShader, linearPaletteFragmentShader)
    };

    this.lastProgram = null;

    this.positions = new Float32Array([0, 0,
      0, 1,
      1, 0,
      1, 0,
      0, 1,
      1, 1]);
    this.positionBuffer = null;
    this.texcoords = new Float32Array([0, 0,
      0, 1,
      1, 0,
      1, 0,
      0, 1,
      1, 1]);
    this.texcoordBuffer = null;

    this.lastImg = null;
    this.lastImgSize = null;
    this.usePixelCanvas = 0;
    this.texture = null;

    this.bounds = null;
    this.webGLContext = getWebglContext(this.canvas);
    this.isContextLost = isContextLost(this.webGLContext);
  }

  get offsetX() { return this.transform && this.transform.offsetX || 0; }
  get offsetY() { return this.transform && this.transform.offsetY || 0; }
  get zoom() { return this.transform && this.transform.zoom || 1; }

  clear() {
    this.prepareCanvas();
  }

  draw(img, lastImgSize, usePixelCanvas) {
    if (!img || !this.imgSize) return;
    this.renderImg(img, lastImgSize, usePixelCanvas, this.prepareCanvas());
  }

  drawBounded(img, lastImgSize, usePixelCanvas, bounds) {
    if (!img || !this.imgSize) return;
    this.bounds = bounds;
    this.renderImg(img, lastImgSize, usePixelCanvas, this.prepareCanvas());
  }

  renderImg(img, lastImgSize, usePixelCanvas, { gl, program }) {
    if (!gl || !program) return;
    this.prepareTexture(gl, img, lastImgSize, usePixelCanvas);
    program.bindTextureBuffers(gl, this.positionBuffer, this.texcoordBuffer);
    program.setSamplerLocation(gl, this.texture);

    // draw the quad (2 triangles, 6 vertices)
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  prepareCanvas() {
    const gl = getWebglContext(this.canvas);
    if (!gl || isContextLost(gl)) 
      this.restoreWebGL();
    this.cleanupCanvas(gl);

    let program = this.selectProgram();
    if (!program) return {};
    if (program !== this.lastProgram) {
      this.lastProgram = program;
      gl.useProgram(program.program);
    }

    let matrix = this.getTransformMatrix(gl);
    program.setTransformationMatrix(gl, matrix);
    this.setProgramColorSettings(gl, program);

    return { gl, program };
  }

  cleanupCanvas(gl) {
    // for clip-space to pixel conversions
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT || gl.DEPTH_BUFFER_BIT);
  }

  getTransformMatrix(gl) {
    let w = gl.canvas.width, h = gl.canvas.height;
    // this matirx will convert from pixels to clip space
    let matrix = m4.orthographic(0, w, h, 0, -1, 1);

    this.scaleToDevicePixelRatio(matrix);

    if (this.bounds) this.applyBounds(matrix, w, h);
    else this.applyTransformations(matrix);
    return matrix;
  }

  scaleToDevicePixelRatio(matrix) {
    let scale = 1;
    if (this.componentName != "OCTReferenceImage")
      scale = window.devicePixelRatio || 1;
    let w = this.canvasSize.w;
    let h = this.canvasSize.h;
    this.canvas.style.width = w + "px";
    this.canvas.style.height = h + "px";
    if (this.isFlipped270 || this.forceTranslate) this.canvas.style.transform = "translate(-60%, -50%)";
    this.canvas.width = w * scale;
    this.canvas.height = h * scale;
    m4.scale(matrix, scale, scale, 1, matrix);
  }

  applyBounds(matrix, w, h) {
    let { minX, maxX, minY, maxY } = this.bounds;
    m4.translate(matrix, minX * w, minY * h, 0, matrix);
    m4.scale(matrix, (maxX - minX) * w, (maxY - minY) * h, 1, matrix);
  }

  applyTransformations(matrix) {
    // this matrix will translate our quad
    m4.translate(matrix, this.offsetX, this.offsetY, 0, matrix);
    if (this.isFlipped90) {
      m4.scale(matrix, this.zoom * this.imgSize.h, this.zoom * this.imgSize.w, 1, matrix);
      m4.zRotation(matrix, 90, matrix);
      if (this.isSideBySide) {
        let ratio = this.imgSize.w / this.imgSize.h;
        let translate = (ratio - 1) / 2;
        m4.translate(matrix, -translate, -1 - translate, 0, matrix);
      }
      else {
        m4.translate(matrix, 0, -1, 0, matrix);
      }
    }
    else if (this.isFlipped270) {
      m4.scale(matrix, this.zoom * this.imgSize.h, this.zoom * this.imgSize.w, 1, matrix);
      m4.zRotation(matrix, 270, matrix);
      if (this.isSideBySide) {
        let ratio = this.imgSize.w / this.imgSize.h;
        let translate = (ratio - 1) / 2;
        m4.translate(matrix, -translate, -1 - translate, 0, matrix);
      }
      else {
        m4.translate(matrix, -1, 0, 0, matrix);
      }
    }
    else {
      m4.scale(matrix, this.zoom * this.imgSize.w, this.zoom * this.imgSize.h, 1, matrix);
    }
    if (this.isFlipped) {
      m4.scale(matrix, -1, -1, 1, matrix);
      m4.translate(matrix, -1, -1, 0, matrix);
    }
    if (this.flippedX) {
      m4.scale(matrix, -1, 1, 1, matrix);
      m4.translate(matrix, -1, 0, 0, matrix);
    }
  }

  setProgramColorSettings(gl, program) {
    if (program.setColorSettings && this.colorSettings)
      program.setColorSettings(gl, this.colorSettings);
    if (program.setOCTColorSettings && this.octColorSettings)
      program.setOCTColorSettings(gl, this.octColorSettings);
    if (program.setThicknessMapColorSettings && this.thicknessMapColorSettings)
      program.setThicknessMapColorSettings(gl, this.thicknessMapColorSettings);
  }

  selectProgram() {
    let program = this.programs.standard;
    if (this.thicknessMapColorSettings) program = this.programs.linearPalette;
    else if (this.octColorSettings) {
      if (this.octColorSettings.isInverted) program = this.programs.invert;
      else if (this.octColorSettings.colorPalette) program = this.programs.palette;
    } else if (this.colorSettings && hasCustomPixelColorSettings(this.colorSettings)) {
      let isOptos = this.colorSettings.isOptos;
      switch (this.colorSettings.grayscaleMode) {
        case GRAYSCALE_MODES.REC709: program = this.programs.luma; break;
        case GRAYSCALE_MODES.Red: program = this.programs.red; break;
        case GRAYSCALE_MODES.Blue: program = this.programs.blue; break;
        case GRAYSCALE_MODES.Green: program = isOptos ? this.programs.optosGreen : this.programs.green; break;
        default: program = isOptos ? this.programs.optosStandard : this.programs.color; break;
      }
    }
    return program;
  }

  prepareTexture(gl, img, lastImgSize, usePixelCanvas) {
    // don't load the texture if I've seen this size texture before
    // note - for resized images, img will be a canvas
    //  the canvas won't change between images, so the "usePixelCanvas" variable is used to determine
    //  if a new texture is loaded
    if (usePixelCanvas === this.usePixelCanvas
      && (img == this.lastImg)
      && sizesEqual(this.lastImgSize, lastImgSize)) return;

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    this.lastImg = img;
    this.lastImgSize = lastImgSize && { ...lastImgSize };
    this.usePixelCanvas = usePixelCanvas;
  }

  prepareWebgl() {
    const gl = getWebglContext(this.canvas);
    if (!gl || isContextLost(gl))    
      this.restoreWebGL();
    this.createPrograms(gl);
    this.prepareBuffers(gl);
  }

  createPrograms(gl) {
    for (let program in this.programs)
      this.programs[program].createProgram(gl);
  }

  prepareBuffers(gl) {
    // create buffers for vertex coordinates and texture positions
    this.positionBuffer = gl.createBuffer();
    this.bindBuffer(gl, this.positionBuffer, this.positions);
    this.texcoordBuffer = gl.createBuffer();
    this.bindBuffer(gl, this.texcoordBuffer, this.texcoords);
  }

  bindBuffer(gl, buffer, data){
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  }

  clearWebGL() {
    const gl = getWebglContext(this.canvas);
    let hasWebgl = !!gl;
    if (hasWebgl) {
      this.programs.standard.clearReferences(gl, this.positionBuffer, this.texcoordBuffer, this.texture);
      for (let program in this.programs)
        this.programs[program] = null;
      gl.canvas.width = 1;
      gl.canvas.height = 1;
      let ext = gl.getExtension("WEBGL_lose_context");
      if (ext) ext.loseContext();
    }
  }

  restoreWebGL() {
    const gl = getWebglContext(this.canvas);
    let ext = gl.getExtension("WEBGL_lose_context");
    if (ext) ext.restoreContext();
  }
}